import {
  BarChart3,
  Building2,
  ClipboardCheck,
  FolderOpen,
  Gavel,
  KeyRound,
  LayoutDashboard,
  LifeBuoy,
  Scale,
  ScrollText,
  Settings,
  Upload,
  type LucideIcon,
} from "lucide-react";

import type { Role } from "../lib/roles";

/**
 * Разделы навигации и роли, которым они видны (SK-5).
 *
 * ВАЖНО: видимость раздела — это UI-подсказка («скрыть кнопку»), НЕ защита. Реальный
 * запрет операций — на сервере по матрице 4.3 ТЗ (permissions.ts, эпик E1; CLAUDE.md
 * правило 5). Список ролей здесь выведён из матрицы 4.3 ТЗ с принятыми отступлениями — при
 * расхождении с прототипом источник истины — ТЗ и матрица `apps/api/src/auth/permissions.ts`.
 * Совпадение с ней держит сторож `navigation-prava.test.ts`: перечень ролей раздела, у́же права
 * сервера, делает право недостижимым (E17, 17.09.2026 — так «Верификация» и «Регистрация»
 * оказались закрыты трём ролям, которым `draft:submit` был выдан).
 *
 * У пользователя может быть несколько ролей — раздел виден, если ХОТЯ БЫ одна его роль
 * присутствует в `roles` (объединение прав, Р-13).
 */
export interface NavItem {
  readonly id: string;
  readonly path: string;
  readonly label: string;
  readonly icon: LucideIcon;
  /** Роли, которым раздел показывается. Основание — строка матрицы 4.3 ТЗ (в комментарии). */
  readonly roles: readonly Role[];
  /**
   * Группа боковой панели, в которой раздел показывается (CR-5, docs/29 §3.3).
   *
   * ГРУППИРОВКА — ТОЛЬКО ВИЗУАЛЬНАЯ. Порядок разделов задаёт сам массив NAV_ITEMS и он НЕ
   * меняется: `homePathFor` отдаёт первый видимый роли раздел, и перестановка «по смыслу групп»
   * молча сменила бы директору стартовый экран. Поэтому группы обязаны ложиться на НЕПРЕРЫВНЫЕ
   * отрезки массива — это инвариант, закрытый тестом: новый раздел, вставленный в середину
   * чужой группы, разорвал бы её, и заметить это глазами было бы нечем.
   */
  readonly group: NavGroupId;
}

/**
 * Группы боковой панели — в порядке показа сверху вниз (docs/29 §3.3, решение владельца
 * 07.09.2026, вариант В3). Заголовок группы, в которой роли не видно ни одного раздела, не
 * рисуется вовсе (см. `navGroupsForRoles`).
 *
 * Названия НЕ совпадают с рубриками справки, хотя docs/29 §3.3 это предполагал: у справки свои
 * пять рубрик («Начало работы», «Договоры и документы», …), и переименование рубрик в объём CR-5
 * не входит (правило 27). Расхождение зафиксировано в плане CR-5 и в отчёте.
 */
export const NAV_GROUPS = [
  { id: "overview", label: "Обзор" },
  { id: "documents", label: "Документы" },
  { id: "processing", label: "Обработка" },
  { id: "legal", label: "Право" },
  { id: "journals", label: "Журналы" },
  { id: "system", label: "Система" },
] as const;

export type NavGroupId = (typeof NAV_GROUPS)[number]["id"];

/** Группа с разделами, видимыми конкретному пользователю. */
export interface NavGroupWithItems {
  readonly id: NavGroupId;
  readonly label: string;
  readonly items: readonly NavItem[];
}

export const NAV_ITEMS: readonly NavItem[] = [
  {
    // 4.3 «Сводка руководителя, эскалации»: коммерч./бухг./юрист/директор — просмотр, админ — да;
    // делопроизводителю недоступна.
    id: "summary",
    path: "/summary",
    label: "Сводка",
    icon: LayoutDashboard,
    roles: ["commercial", "accounting", "lawyer", "director", "admin"],
    group: "overview",
  },
  {
    // Дашборд (E12a) — та же строка 4.3 и то же право `summary:view`, что у сводки: показывает
    // ровно её данные, только картинками (решение владельца 03.09.2026, вариант «а»).
    //
    // СТОИТ ПОСЛЕ «СВОДКИ» НЕ ИЗ ВЕЖЛИВОСТИ: `homePathFor` отдаёт ПЕРВЫЙ видимый роли раздел, и
    // вставка выше молча сменила бы директору стартовый экран после входа.
    id: "dashboard",
    path: "/dashboard",
    label: "Дашборд",
    icon: BarChart3,
    roles: ["commercial", "accounting", "lawyer", "director", "admin"],
    group: "overview",
  },
  {
    // 4.3 «Просмотр реестров договоров и обязательств»: доступен всем ролям (просмотр/полный).
    id: "contracts",
    path: "/contracts",
    label: "Договоры",
    icon: FolderOpen,
    roles: ["clerk", "commercial", "accounting", "lawyer", "director", "admin"],
    group: "documents",
  },
  {
    // 4.3 «Просмотр реестров договоров и обязательств» (та же строка).
    id: "obligations",
    path: "/obligations",
    label: "Обязательства",
    icon: ClipboardCheck,
    roles: ["clerk", "commercial", "accounting", "lawyer", "director", "admin"],
    group: "documents",
  },
  {
    // Справочник контрагентов (E3). Просмотр — всем ролям (реестр справочных данных рядом с
    // договорами); создание/правка/удаление — на сервере под правом `contract:register`
    // (делопроизводитель/юрист/админ). Видимость раздела здесь — только UI-подсказка (правило 5).
    id: "counterparties",
    path: "/counterparties",
    label: "Контрагенты",
    icon: Building2,
    roles: ["clerk", "commercial", "accounting", "lawyer", "director", "admin"],
    group: "documents",
  },
  {
    // 4.3 «Регистрация договора, загрузка сканов, запуск ИИ-разбора» — делопроизводитель, юрист,
    // админ. ОТКРЫТО ВСЕМ ШЕСТИ ролям эпиком E17 (ответ заказчика В41 от 16.09.2026): комплект
    // сканов отправляет тот, кто принёс договор, любой роли. Право на сервере — `draft:submit`
    // (`permissions.ts`), и без этой строки оно недостижимо: меню не показано, маршрут закрыт.
    id: "register",
    path: "/register",
    label: "Регистрация",
    icon: Upload,
    roles: ["clerk", "commercial", "accounting", "lawyer", "director", "admin"],
    group: "processing",
  },
  {
    // 4.3 «Верификация черновиков, постановка на контроль» — юрист и админ. ОТКРЫТО ВСЕМ ШЕСТИ
    // ролям эпиком E17 (ответ заказчика В35 от 16.09.2026): раскладку СВОЕГО черновика
    // подтверждает его создатель. Раздел у всех один, а объём разный и решается СЕРВЕРОМ: без
    // права `draft:verify` список сужен до своих, чужой черновик не открывается и по прямому
    // адресу (`drafts/service.ts`). Прятать раздел от создателя значило бы отнять у него ровно
    // то действие, ради которого эпик и делался.
    id: "verify",
    path: "/verify",
    label: "Верификация",
    icon: Scale,
    roles: ["clerk", "commercial", "accounting", "lawyer", "director", "admin"],
    group: "processing",
  },
  {
    // 4.3 «Претензии, перевод в спор, снятие с контроля»: только юрист и админ. Отдельной
    // строки «просмотр рисков» в матрице нет; надзор директора закрыт разделом «Сводка»
    // (строка 4.3 «Сводка руководителя, эскалации»), поэтому в «Претензии и риски» его не пускаем.
    id: "legal",
    path: "/legal",
    label: "Претензии и риски",
    icon: Gavel,
    roles: ["lawyer", "admin"],
    group: "legal",
  },
  {
    // ВНЕ таблицы 4.3: журнал аудита. Видят юрист и админ (право `audit:view`, E2 —
    // основание в apps/api/src/auth/permissions.ts: ТЗ 4.1.2 + бэклог E2). Раздел — надзорная
    // выборка следов по всем объектам; остальным ролям скрыт (и закрыт на сервере).
    id: "audit",
    path: "/audit",
    label: "Журнал аудита",
    icon: ScrollText,
    roles: ["lawyer", "admin"],
    group: "journals",
  },
  {
    // ВНЕ таблицы 4.3: журнал входов (D-2). Видят юрист и админ (право `login_audit:view` —
    // основание §7/§10 ТЗ и инцидент 20.07.2026, permissions.ts). Остальным скрыт и закрыт на сервере.
    id: "login-audit",
    path: "/login-audit",
    label: "Журнал входов",
    icon: KeyRound,
    roles: ["lawyer", "admin"],
    group: "journals",
  },
  {
    // 4.3 «Настройки, справочники (календарь, ставка ЦБ), пользователи»: только админ.
    id: "settings",
    path: "/settings",
    label: "Настройки",
    icon: Settings,
    roles: ["admin"],
    group: "system",
  },
  {
    // ВНЕ таблицы 4.3: справка в продукте (E16). ЕДИНСТВЕННЫЙ раздел без ролевых ограничений,
    // и это намеренно: правила системы объясняются всем шести ролям (план E16 §5, п. 5).
    // Внутри раздела фильтр по ролям сужает список статей, но не запрещает — есть «показать все».
    //
    // СТОИТ ПОСЛЕДНИМ НЕ ИЗ ВЕЖЛИВОСТИ: `homePathFor` отдаёт ПЕРВЫЙ видимый роли раздел, и
    // вставка справки выше молча сменила бы кому-нибудь стартовый экран.
    id: "help",
    path: "/help",
    label: "Справка",
    icon: LifeBuoy,
    roles: ["clerk", "commercial", "accounting", "lawyer", "director", "admin"],
    group: "system",
  },
];

/**
 * Разделы, доступные пользователю с указанными ролями (объединение по всем ролям, Р-13).
 * Порядок сохраняется из NAV_ITEMS. Первый элемент — стартовый раздел роли (см. homePathFor).
 */
export function navForRoles(userRoles: readonly Role[]): NavItem[] {
  const roleSet = new Set(userRoles);
  return NAV_ITEMS.filter((item) => item.roles.some((r) => roleSet.has(r)));
}

/**
 * Разделы, разложенные по группам боковой панели (CR-5). Порядок групп — из NAV_GROUPS,
 * порядок разделов внутри — из NAV_ITEMS.
 *
 * ПУСТЫХ ГРУПП НЕ БЫВАЕТ: группа, в которой роли не видно ни одного раздела, не возвращается
 * вовсе — иначе делопроизводитель увидел бы заголовки «Право» и «Журналы» без единой строки под
 * ними, то есть перечень того, что ему недоступно. Это единственное новое правило видимости,
 * которое вводит боковая панель, и оно закрыто тестом по каждой из шести ролей.
 */
export function navGroupsForRoles(userRoles: readonly Role[]): NavGroupWithItems[] {
  const items = navForRoles(userRoles);
  return NAV_GROUPS.map((group) => ({
    id: group.id,
    label: group.label,
    items: items.filter((item) => item.group === group.id),
  })).filter((group) => group.items.length > 0);
}

/**
 * Стартовый путь после входа: первый видимый роли раздел (клерк не видит «Сводку» —
 * попадёт на «Договоры»). Если по ролям не доступно ничего (не должно случаться при
 * корректных данных) — «Договоры» как безопасный просмотровый раздел.
 */
export function homePathFor(userRoles: readonly Role[]): string {
  return navForRoles(userRoles)[0]?.path ?? "/contracts";
}
